import {createStyles, Group, Text, UnstyledButton} from '@mantine/core';
import React, {useState} from 'react';

import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';

import type {BulkPredictionItem} from './BulkPredictionUtils';

const VISIBLE_CHIP_LIMIT = 10;

interface SelectedEntitiesAreaProps {
  items: Array<BulkPredictionItem>;
  maxBulkPredictionItems: number;
  onRemoveItem: (id: string) => void;
  onClearAll: () => void;
}

export default function SelectedEntitiesArea({
  items,
  maxBulkPredictionItems,
  onRemoveItem,
  onClearAll,
}: SelectedEntitiesAreaProps): JSX.Element {
  const {classes} = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_CHIP_LIMIT);
  const hiddenCount = items.length - VISIBLE_CHIP_LIMIT;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Text size="sm" weight={600}>
          Selected entities ({items.length}/{maxBulkPredictionItems})
        </Text>
        {items.length > 0 && (
          <UnstyledButton onClick={onClearAll}>
            <Text size="xs" color="dimmed">
              Clear all
            </Text>
          </UnstyledButton>
        )}
      </div>
      <div className={classes.chipsArea}>
        {items.length === 0 ? (
          <div className={classes.placeholder}>None selected</div>
        ) : (
          <Group spacing="xs">
            {visibleItems.map((item) => (
              <BioentityChip
                key={item.id}
                id={item.id}
                className={classes.chip}
                size={ChipSize.SMALL}
                isLink={false}
                onRemove={() => {
                  onRemoveItem(item.id);
                }}
              />
            ))}
            {!isExpanded && hiddenCount > 0 && (
              <UnstyledButton
                className={classes.overflowButton}
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                +{hiddenCount} more
              </UnstyledButton>
            )}
          </Group>
        )}
      </div>
    </div>
  );
}

// Chip height for chip--small: 16px line-height + 5px top padding + 5px bottom padding = 26px.
// Row gap from Group spacing="xs" = 4px.
// Showing 2 full rows + 0.5 row to signal scrollability:
//   top-padding(12) + row1(26) + gap(4) + row2(26) + gap(4) + half-row(13) = 85px
const CHIPS_AREA_MAX_HEIGHT = 85;

const useStyles = createStyles((theme) => ({
  container: {
    marginBottom: theme.spacing.md,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  chipsArea: {
    minHeight: 50,
    maxHeight: CHIPS_AREA_MAX_HEIGHT,
    overflowY: 'auto',
    padding: theme.spacing.md,
    borderRadius: theme.radius.sm,
    // theme.colors.gray[0] = #F7F7F9 (closest pre-defined color to #F7F8FA)
    backgroundColor: theme.colors.gray[0],
  },
  placeholder: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    lineHeight: '24px',
  },
  overflowButton: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.blue[6],
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  // theme.colors.gray[1] = #F0F1F2 (exact match for the requested chip background).
  // Scoped to non-negative chips so chip--negative error styling and hover/focus
  // states are preserved (no !important needed with this selector specificity).
  chip: {
    '&:not(.chip--negative)': {
      backgroundColor: theme.colors.gray[1],
      borderColor: theme.colors.gray[1],
      '&:hover': {
        backgroundColor: theme.colors.gray[2],
        borderColor: theme.colors.gray[2],
      },
    },
  },
}));
